import { defineComponent as _defineComponent } from 'vue'
import Vue, { computed, ref } from 'vue';
import VueObserveVisibility from 'vue-observe-visibility';

import { ObjectType, TitleListType } from '@/@types/graphql-types';

import PictureComp from '@/components/picture/PictureComp.vue';

import { ThumborImage, ThumborOptions } from '@/filters/thumbor';

import { diffInDays, diffInMilliseconds } from '@/helpers/date-helper';
import { usePosterImpressionTracking } from '@/helpers/composables/usePosterImpressionTracking';
import { AvailabilityBadgeSize, AvailabilityBadgeType } from '@/components/poster/types/availability-badge';

import { getDisneyImpressionUrl } from '@/helpers/provider-promotion';
import { isTitleInList, isTitleSeason } from '@/helpers/title-helper';
import { useLanguageStore, useUserStore } from '@/helpers/composables/useStores';

// @TODO temp types
import type { PosterSponsoredRecImages, PosterTitle } from '@/interfaces/components/poster/PosterGraphql';
import type { WatchNowOfferFragment } from '@/pages/graphql/fragments/WatchNowOffer.fragment';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { ObservableOptions } from '@/interfaces/observable';
import type { Maybe } from '@/helpers/types';

import { faClock } from '@fortawesome/free-solid-svg-icons';
import { AdditionalProperties } from '@/helpers/composables/useBatchImpressionTracking';
import { useI18n } from '@/helpers/composables/useI18n';

export interface PosterGraphqlProps {
	title: PosterTitle;
	sponsoredRecImages?: PosterSponsoredRecImages;
	watchNowOffer?: Maybe<WatchNowOfferFragment>;
	additionalContexts?: SnowplowContext[];
	trackingProperties?: AdditionalProperties;
	index?: number;
	observableOptions?: ObservableOptions;
	imageProfile?: keyof ThumborImage<any>;
	showRibbon?: boolean;
	showQuickActions?: boolean;
	forceQuickActions?: boolean;
	hasMoreLikeThis?: boolean;
	showWatchNowButton?: boolean;
	showTvBadge?: boolean;
	showNewEpisodesCount?: boolean;
	freeOffersCount?: number;
	showFreeTag?: boolean;
	availabilityBadgeSize?: AvailabilityBadgeSize;
	showAvailabilityBadge?: boolean;
	moduleGroup?: string;
	lazy?: boolean;
	clickSource?: string;
	noRadius?: 'top' | 'right' | 'bottom' | 'left';
	smallBubbles?: boolean;
	loading?: 'lazy' | 'eager';
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PosterGraphql',
  props: {
    title: null,
    sponsoredRecImages: null,
    watchNowOffer: null,
    additionalContexts: { default: () => [] },
    trackingProperties: { default: () => ({}) },
    index: null,
    observableOptions: null,
    imageProfile: { default: 'poster' },
    showRibbon: { type: Boolean },
    showQuickActions: { type: Boolean },
    forceQuickActions: { type: Boolean },
    hasMoreLikeThis: { type: Boolean },
    showWatchNowButton: { type: Boolean, default: true },
    showTvBadge: { type: Boolean, default: true },
    showNewEpisodesCount: { type: Boolean },
    freeOffersCount: { default: 0 },
    showFreeTag: { type: Boolean },
    availabilityBadgeSize: { default: AvailabilityBadgeSize.Long },
    showAvailabilityBadge: { type: Boolean },
    moduleGroup: { default: '' },
    lazy: { type: Boolean, default: true },
    clickSource: null,
    noRadius: null,
    smallBubbles: { type: Boolean },
    loading: { default: 'eager' }
  },
  emits: ["click", "imageError"],
  setup(__props: any) {

const props = __props as { title: PosterTitle, sponsoredRecImages?: PosterSponsoredRecImages, watchNowOffer?: Maybe<WatchNowOfferFragment>, additionalContexts: SnowplowContext[], trackingProperties: AdditionalProperties, index?: number, observableOptions?: ObservableOptions, imageProfile: keyof ThumborImage<any>, showRibbon?: boolean, showQuickActions?: boolean, forceQuickActions?: boolean, hasMoreLikeThis?: boolean, showWatchNowButton: boolean, showTvBadge: boolean, showNewEpisodesCount?: boolean, freeOffersCount: number, showFreeTag?: boolean, availabilityBadgeSize: AvailabilityBadgeSize, showAvailabilityBadge?: boolean, moduleGroup: string, lazy: boolean, clickSource?: string, noRadius?: 'top' | 'right' | 'bottom' | 'left', smallBubbles?: boolean, loading: 'lazy' | 'eager' };

Vue.use(VueObserveVisibility);

const FreeTag = () => import('./FreeTag.vue');
const ImpressionPixel = () =>
	import(/* webpackChunkName: "impression-pixel" */ '@/components/sponsored-recommendations/ImpressionPixel.vue');
const AvailabilityBadge = () => import('@/components/poster/AvailabilityBadge.vue');
const PosterQuickActionsGraphql = () => import('@/components/poster/PosterQuickActionsGraphql.vue');
const TitlePosterRibbonGraphqlLite = () => import('@/components/poster/TitlePosterRibbonGraphqlLite.vue');

// @TODO this type is duplicated from PosterGraphql.ts because of a Vue 2 parser limitation




const { settings, isLoggedIn } = useUserStore();
const { webLocale } = useLanguageStore();
const hasFreeOffers = computed(() => props.freeOffersCount && props.freeOffersCount > 0 && props.showFreeTag);

const titleSlug = computed(() => {
	const fullPath = posterUrl.value?.match(/([^\/]*)$/);
	return fullPath && fullPath[0] ? fullPath[0] : 'Poster';
});

const imageUrl = computed(() => {
	// TODO [PictureComp] Refactor PictureComp to be able to use new url templating /poster/poster_id/{profile}/movie_name.{format}
	return Vue.filter('Thumbor')({
		url: posterUrl.value,
		imageType: props.imageProfile,
	} as ThumborOptions);
});

const posterUrl = computed(() => props.title.content?.posterUrl ?? '');

const showTVShowBadge = computed(
	() => props.showTvBadge && [ObjectType.Show, ObjectType.ShowSeason].includes(props.title.objectType)
);

const isShowAvailabilityBadge = computed(
	() => props.showAvailabilityBadge && isLoggedIn.value && !!availabilityBadgeDate.value
);

const upcomingReleaseFromSelectedPackages = computed(() => {
	const userPackages = settings.value.providers;
	return (
		(props.title?.content?.upcomingReleases ?? [])
			?.filter(item => !!item?.releaseDate && diffInMilliseconds(new Date(item.releaseDate)) > 0)
			.filter(item => (item?.releaseCountDown ?? diffInDays(new Date(item?.releaseDate))) < 30)
			.find(item => userPackages.includes(item?.package?.shortName!)) || null
	);
});

const leavingOfferFromSelectedPackages = computed(() => {
	const userPackages = settings.value.providers;
	return (
		(props.title?.availableTo ?? [])
			?.filter(
				item =>
					item?.availableCountDown ||
					(!!item?.availableToDate && diffInDays(new Date(item.availableToDate)) < 30)
			)
			.find(item => userPackages.includes(item?.package?.shortName)) || null
	);
});

const availabilityBadgeType = computed(() =>
	!!upcomingReleaseFromSelectedPackages.value ? AvailabilityBadgeType.Upcoming : AvailabilityBadgeType.Leaving
);

const availabilityBadgeDate = computed(() => {
	if (!!upcomingReleaseFromSelectedPackages.value) {
		return upcomingReleaseFromSelectedPackages.value.releaseDate;
	}
	if (!!leavingOfferFromSelectedPackages.value) {
		return leavingOfferFromSelectedPackages.value.availableToDate;
	}
	return null;
});

const availabilityBadgeCountdown = computed(() => {
	if (!!upcomingReleaseFromSelectedPackages.value) {
		return upcomingReleaseFromSelectedPackages.value.releaseCountDown;
	}
	if (!!leavingOfferFromSelectedPackages.value) {
		return leavingOfferFromSelectedPackages.value.availableCountDown;
	}
	return null;
});

const titleSeasonNumber = computed(() => (isTitleSeason(props.title) ? props.title?.content?.seasonNumber : undefined));

const { tc } = useI18n();
const titleName = computed(() =>
	isTitleSeason(props.title)
		? `${props.title?.show?.content?.title} - ${tc('WEBAPP_SEASON')} ${titleSeasonNumber.value}`
		: props.title?.content?.title
);

// IMPRESSION PIXEL //
const mountImpressionPixel = ref(false);

const externalImpressionTag = computed(() =>
	getDisneyImpressionUrl(props.watchNowOffer?.package?.packageId, webLocale.value)
);

const { onVisibilityChanged } = usePosterImpressionTracking({
	groupId: props.moduleGroup ?? '',
	preventDuplicateAdditionalContext: true,
	additionalContexts: () => props.additionalContexts!,
	additionalProperties: () => props.trackingProperties,
});

function onVisibilityCallback() {
	if (!mountImpressionPixel.value && externalImpressionTag.value) {
		mountImpressionPixel.value = true;
	}
}

// IMPRESSION PIXEL END //

return { __sfc: true,FreeTag, ImpressionPixel, AvailabilityBadge, PosterQuickActionsGraphql, TitlePosterRibbonGraphqlLite, props, settings, isLoggedIn, webLocale, hasFreeOffers, titleSlug, imageUrl, posterUrl, showTVShowBadge, isShowAvailabilityBadge, upcomingReleaseFromSelectedPackages, leavingOfferFromSelectedPackages, availabilityBadgeType, availabilityBadgeDate, availabilityBadgeCountdown, titleSeasonNumber, tc, titleName, mountImpressionPixel, externalImpressionTag, onVisibilityChanged, onVisibilityCallback, TitleListType, PictureComp, isTitleInList, faClock }
}

})